import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageSquare, ChevronDown, ChevronUp, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { AdminNav } from "../components/AdminNav";
import { api } from "../lib/api";

function LeadTranscript({ lead, messages }) {
  if (messages === null) {
    return (
      <div className="flex items-center gap-2 text-offwhite/40 text-xs py-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading transcript…
      </div>
    );
  }
  if (messages.length === 0) {
    return <p className="text-offwhite/40 text-xs py-2">No transcript available.</p>;
  }
  return (
    <div
      data-testid={`chat-lead-transcript-${lead.id}`}
      className="border border-white/10 bg-charcoal max-h-72 overflow-y-auto p-4 space-y-3"
    >
      {messages.map((m) => (
        <div
          key={`${m.created_at}-${m.role}`}
          className={`text-sm ${m.role === "user" ? "text-offwhite" : "text-offwhite/60"}`}
        >
          <span
            className={`text-[10px] font-bold uppercase tracking-[0.2em] mr-2 ${
              m.role === "user" ? "text-clay" : "text-emerald-300/70"
            }`}
          >
            {m.role === "user" ? "Buyer" : "Assistant"}
          </span>
          <span className="whitespace-pre-wrap">{m.content}</span>
        </div>
      ))}
    </div>
  );
}

function LeadCard({ lead, onDelete }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && messages === null && lead.session_id) {
      try {
        const res = await api.get(`/api/chat/leads/${lead.session_id}/messages`);
        setMessages(res.data.messages);
      } catch (err) {
        console.error("Failed to load transcript:", err);
        setMessages([]);
      }
    }
  };

  return (
    <div data-testid={`chat-lead-card-${lead.id}`} className="border border-white/10 bg-white/[0.03]">
      <button
        data-testid={`chat-lead-toggle-${lead.id}`}
        onClick={toggle}
        className="w-full flex flex-wrap items-center gap-x-5 gap-y-2 px-5 sm:px-7 py-5 text-left"
      >
        <span className="font-display font-bold text-offwhite text-sm">{lead.name || "Unknown visitor"}</span>
        {lead.company && <span className="text-sm text-offwhite/50">{lead.company}</span>}
        {lead.email && <span className="text-xs text-clay font-semibold break-all">{lead.email}</span>}
        {lead.interest && <span className="text-xs text-offwhite/40">{lead.interest}</span>}
        <span className="ml-auto flex items-center gap-3 text-xs text-offwhite/40">
          <span className="inline-flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" />
            {lead.message_count}
          </span>
          {new Date(lead.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="px-5 sm:px-7 pb-6 pt-2 border-t border-white/10 space-y-4">
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm">
            {[
              ["Name", lead.name],
              ["Email", lead.email],
              ["Company", lead.company],
              ["Interest", lead.interest],
            ].map(([label, value]) => (
              <p key={label} className="flex flex-col gap-0.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-offwhite/35">{label}</span>
                <span className="text-offwhite/80 break-all">{value || "—"}</span>
              </p>
            ))}
          </div>
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-offwhite/35">Conversation</span>
            <LeadTranscript lead={lead} messages={messages} />
          </div>
          <div className="flex flex-wrap gap-3">
            {lead.email && (
              <a
                data-testid={`chat-lead-email-${lead.id}`}
                href={`mailto:${lead.email}?subject=${encodeURIComponent("Following up on your HAQILA chat inquiry")}`}
                className="inline-flex items-center gap-2 text-xs font-semibold bg-forest hover:bg-moss text-offwhite px-4 py-2 transition-colors duration-300"
              >
                <Mail className="w-3.5 h-3.5" />
                Email Lead
              </a>
            )}
            <button
              data-testid={`chat-lead-delete-${lead.id}`}
              onClick={() => onDelete(lead)}
              className="inline-flex items-center gap-2 text-xs font-semibold border border-red-900/50 text-red-400/70 hover:border-red-400 hover:text-red-400 px-4 py-2 transition-colors duration-300"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Lead
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminChatLeads() {
  const [leads, setLeads] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/api/chat/leads")
      .then((res) => setLeads(res.data.leads))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete chat lead ${lead.name || lead.email || "Unknown"} and its conversation?`)) return;
    try {
      await api.delete(`/api/chat/leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      toast.success("Chat lead deleted");
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  let listContent;
  if (leads === null) {
    listContent = (
      <div className="flex items-center gap-3 text-offwhite/50">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading chat leads…
      </div>
    );
  } else if (leads.length === 0) {
    listContent = (
      <div
        data-testid="chat-leads-empty"
        className="border border-white/10 bg-white/[0.03] p-14 flex flex-col items-center gap-4 text-center"
      >
        <MessageSquare className="w-8 h-8 text-offwhite/30" />
        <p className="text-offwhite/50 text-sm">
          No chat leads yet. When visitors share their name, email or company with the AI assistant, they will appear
          here.
        </p>
      </div>
    );
  } else {
    listContent = (
      <div data-testid="chat-leads-list" className="space-y-4">
        {leads.map((lead) => (
          <LeadCard key={lead.id} lead={lead} onDelete={deleteLead} />
        ))}
      </div>
    );
  }

  return (
    <>
      <SEO title="Chat Leads | HAQILA Admin" description="HAQILA chat lead management." />
      <div data-testid="admin-chat-leads-page" className="bg-charcoal min-h-screen pt-32 pb-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-clay mb-2">HAQILA Admin</p>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-offwhite">Chat Leads</h1>
            </div>
            <AdminNav active="Chat Leads" />
          </div>

          <p className="text-sm text-offwhite/50 mb-8 max-w-2xl">
            Contact details captured automatically by the AI assistant during website chats. Expand a lead to read the
            full conversation before following up.
          </p>

          {listContent}
        </div>
      </div>
    </>
  );
}
